import * as React from 'react';
import { useCallback, useEffect } from 'react';

import * as Sentry from '@sentry/react';

import { buildHashWithAuthMode, getLocationHash } from 'js/lib/authModeDeepLinkUtils';
import { setLocation } from 'js/lib/coursera.redirect';
import { useLocation, useNavigate } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { Button } from '@coursera/cds-core';
import type { ButtonProps, IconProps } from '@coursera/cds-core';
import type { ButtonName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';
import type { EventData } from '@coursera/event-pulse/react';

import usePathwayAndCourseEnrollmentActions from 'bundles/skills-common/shared/verified-skills/data/pathway-badge/usePathwayAndCourseEnrollmentActions';
import useNavigateToSkillContent from 'bundles/skills-common/shared/verified-skills/hooks/useNavigateToSkillContent';

import _t from 'i18n!nls/skills-common';

export type Props = ButtonProps & {
  isEnrolledInPathway: boolean;
  isEnrolledInCourse: boolean;
  isProgramMember: boolean;
  courseId: string;
  courseSlug: string;
  programId: string;
  redirectToAssessment?: boolean;
  assessmentId?: string;
  pathwayCollectionId?: string;
  pathwayId?: string;
  programSlug?: string;
  thirdPartyOrganizationSlug?: string;
  disabled?: boolean;
  buttonText?: string;
  buttonIcon?: React.ReactElement<IconProps>;
  buttonIconPosition?: 'before' | 'after';
  // keeping these props for Skill Dialog's implementation
  activateProgramAccessModal?: () => void;
  // isEnrollingCallback and disabled are used when multiple buttons that perform enrollment actions are present on the page.
  isEnrollingCallback?: (currentEnrollingAction: string | undefined) => void;
  trackingData?: EventData<'click_button'>;
  trackingButtonName?: ButtonName;
  className?: string;
};

// This component closely mirrors https://github.com/webedx-spark/web/blob/7c768a3cda963c0f9f5b68dcae7418578dec3167/static/bundles/enterprise-learning-path-description-page/components/EnterpriseSkillPathwayCourseActionButton.tsx
// This button will handle enrolling the user into learning path and course if user is missing access.
// If redirecting to assessment, the button will fetch the assessment ID after user enrolls in course as enrollment is required for this item.
const SkillAssessmentCourseActionButton = ({
  isEnrolledInPathway,
  isEnrolledInCourse,
  isProgramMember,
  pathwayCollectionId,
  pathwayId,
  courseId,
  programId,
  courseSlug,
  activateProgramAccessModal,
  programSlug,
  thirdPartyOrganizationSlug,
  isEnrollingCallback,
  disabled,
  assessmentId,
  redirectToAssessment,
  buttonText,
  buttonIcon,
  buttonIconPosition,
  trackingData,
  trackingButtonName,
  className,
  ...props
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isEnrolling, setIsEnrolling] = React.useState(false);
  const [isNavigating, setIsNavigating] = React.useState(false);
  const { executeEnrollments } = usePathwayAndCourseEnrollmentActions();
  const track = useTracker();

  useEffect(() => {
    // Mirroring the enterprise implementation so it can be used for the Skills Dialog
    if (isEnrolling && redirectToAssessment) {
      isEnrollingCallback?.(`pathway-${courseId}-assessment`);
    } else if (isEnrolling) {
      isEnrollingCallback?.(`pathway-${courseId}`);
    } else {
      isEnrollingCallback?.(undefined);
    }
  }, [isEnrolling, isEnrollingCallback, courseId, redirectToAssessment]);

  useEffect(() => {
    return () => {
      setIsNavigating(false);
    };
  }, []);

  const onError = useCallback<(error: unknown) => void>(
    (error: unknown) => {
      if (error instanceof Error) {
        Sentry.captureException(error);
      } else {
        Sentry.captureException(new Error(typeof error === 'string' ? error : JSON.stringify(error)));
      }

      setIsEnrolling(false);
      setIsNavigating(false);
      // Since we don't have proper error states, reload page on failure to reset/refresh enrollment states (as we don't know what was a success or failure here).
      setLocation(location.pathname);
    },
    [location.pathname]
  );

  const { navigateToAssessment, navigateToCourse } = useNavigateToSkillContent({
    courseSlug,
    courseId,
    onError,
  });

  const onActionSuccess = () => {
    // Redirect on success, retain the "isEnrolling" state as it continues to show the loading state and prevent double click.
    if (redirectToAssessment && assessmentId) {
      navigateToAssessment(assessmentId);
    } else {
      navigateToCourse();
    }
  };

  const onClick = async () => {
    setIsNavigating(true);

    track('click_button', {
      ...trackingData,
      button: trackingData?.button || {
        name: trackingButtonName || 'verified_skills_assessment_footer_next_skill_area_course',
      },
      enterprise: {
        ...(thirdPartyOrganizationSlug ? { organizationSlug: thirdPartyOrganizationSlug } : {}),
        ...(programSlug ? { programSlug } : {}),
      },
      product: {
        id: courseId,
        type: 'course',
      },
    });

    const isEnrolledInPathwayAndCourse = isEnrolledInPathway && isEnrolledInCourse;

    // Don't show the enrolling state if already enrolled.
    if (isEnrolledInPathwayAndCourse) {
      onActionSuccess();
      return;
    }

    if (!isAuthenticatedUser()) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete('authMode');
      const nextSearch = searchParams.toString();
      navigate({
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: buildHashWithAuthMode(getLocationHash(location), 'login'),
      });
    } else {
      setIsEnrolling(true);

      try {
        await executeEnrollments({
          isEnrolledInPathway,
          isEnrolledInCourse,
          pathwayId,
          programId,
          courseId,
          pathwayCollectionId,
          programSlug,
          thirdPartyOrganizationSlug,
        });

        onActionSuccess();
      } catch (error) {
        onError(error);
      }
    }
  };

  return (
    <Button
      {...props}
      className={className}
      onClick={onClick}
      disabled={disabled}
      loading={isEnrolling || isNavigating}
      icon={buttonIcon}
      iconPosition={buttonIconPosition}
    >
      {props.children || _t('Go to next item')}
    </Button>
  );
};

export default SkillAssessmentCourseActionButton;
